// src/hooks/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { requestJson, setAuthToken, clearAuthToken } from '../api/http';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // { _id, email, role, ... }
  const [token, setToken] = useState(null);      // JWT string
  const [initialising, setInitialising] = useState(true);

  // On app start, restore auth from localStorage (if available)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.token) {
          setToken(parsed.token);
          setUser(parsed.user || null);
          setAuthToken(parsed.token); // ensures Authorization header is sent
        }
      }
    } catch (e) {
      console.error('Failed to restore auth from storage', e);
    } finally {
      setInitialising(false);
    }
  }, []);

  /**
   * Log in with email/password.
   * - Calls POST /auth/login via requestJson
   * - On success: saves token & user in state + localStorage
   * - On validation / auth error: lets the error throw so the caller (LoginPage)
   *   can show messages based on err.validationErrors or err.status.
   */
  const login = async (email, password) => {
    const data = await requestJson('/auth/login', {
      method: 'POST',
      body: { email, password },
    });

    const { token: jwt, user: userData } = data;

    setToken(jwt);
    setUser(userData);
    setAuthToken(jwt);

    localStorage.setItem(
      'auth',
      JSON.stringify({ token: jwt, user: userData })
    );
  };

  /**
   * Clear auth state completely.
   */
  const logout = () => {
    setToken(null);
    setUser(null);
    clearAuthToken();
    localStorage.removeItem('auth');
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    initialising,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
